import React, { createContext, useContext, useState, useEffect } from 'react';
import authManager from '../utils/auth';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // 初始化用户状态
  useEffect(() => {
    const initUser = async () => {
      try {
        const loggedIn = authManager.isLoggedIn();
        
        if (loggedIn) {
          // 如果已登录，通过API获取用户信息
          const apiUserInfo = await authManager.getUserFromAPI();
          if (apiUserInfo) {
            authManager.saveUserInfo(apiUserInfo);
            setUser(apiUserInfo);
            setIsLoggedIn(true);
          } else {
            // /auth/me 不可用时，使用本地缓存的用户信息兜底
            const cachedUser = authManager.getUserInfoLocal();
            if (cachedUser) {
              setUser(cachedUser);
              setIsLoggedIn(true);
            } else {
              setUser(null);
              setIsLoggedIn(false);
              authManager.logout(); // 清除无效的token
            }
          }
        } else {
          setUser(null);
          setIsLoggedIn(false);
        }
      } catch (error) {
        setUser(null);
        setIsLoggedIn(false);
        // 如果API调用失败，清除可能无效的token
        authManager.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initUser();

    // 监听localStorage变化，同步登录状态
    const handleStorageChange = async (e) => {
      if (e.key === 'auth_token') {
        const loggedIn = authManager.isLoggedIn();
        if (loggedIn) {
          // 通过API获取用户信息
          const apiUserInfo = await authManager.getUserFromAPI();
          if (apiUserInfo) {
            authManager.saveUserInfo(apiUserInfo);
            setUser(apiUserInfo);
            setIsLoggedIn(true);
          } else {
            // /auth/me 不可用时，使用本地缓存的用户信息兜底
            const cachedUser = authManager.getUserInfoLocal();
            if (cachedUser) {
              setUser(cachedUser);
              setIsLoggedIn(true);
            } else {
              setUser(null);
              setIsLoggedIn(false);
              authManager.logout(); // 清除无效的token
            }
          }
        } else {
          setUser(null);
          setIsLoggedIn(false);
        }
      }
    };

    // 监听storage事件（跨标签页同步）
    window.addEventListener('storage', handleStorageChange);

    // 监听自定义的token清除事件
    const handleAuthTokenCleared = () => {
      setUser(null);
      setIsLoggedIn(false);
    };
    window.addEventListener('authTokenCleared', handleAuthTokenCleared);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authTokenCleared', handleAuthTokenCleared);
    };
  }, []); // 移除 isLoggedIn 依赖，只在组件挂载时执行一次

  // 定期检查token状态（防止状态不同步）
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const currentLoggedIn = authManager.isLoggedIn();
      if (currentLoggedIn !== isLoggedIn) {
        if (currentLoggedIn) {
          // 只在状态变化时更新，不重复调用API
          setIsLoggedIn(true);
        } else {
          setUser(null);
          setIsLoggedIn(false);
        }
      }
    }, 5000); // 每5秒检查一次

    return () => {
      clearInterval(checkInterval);
    };
  }, [isLoggedIn]); // 这个useEffect可以保留isLoggedIn依赖

  // 登录
  const login = async (userInfo, token, expiresIn) => {
    try {
      if (!token) return false;
      const success = authManager.saveToken(token);
      if (!success) return false;
  
      // 登录接口已返回 user_info（微信/邮箱/短信登录均会返回）时直接使用，
      // 不再强依赖 /auth/me（agent 微信服务上无此接口）
      if (userInfo) {
        authManager.saveUserInfo(userInfo);
        setUser(userInfo);
        setIsLoggedIn(true);
        return true;
      }
  
      // 没有 userInfo 时通过 /auth/me 获取
      const apiUserInfo = await authManager.getUserFromAPI();
      if (apiUserInfo) {
        authManager.saveUserInfo(apiUserInfo);
        setUser(apiUserInfo);
        setIsLoggedIn(true);
        return true;
      }
      authManager.logout(); // 清除无效的token
      return false;
    } catch (error) {
      authManager.logout(); // 清除可能无效的token
      return false;
    }
  };
  // 登出
  const logout = () => {
    try {
      const success = authManager.logout();
      if (success) {
        setUser(null);
        setIsLoggedIn(false);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // 更新用户信息
  const updateUser = (updates) => {
    try {
      const success = authManager.updateUser(updates);
      if (success) {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // 获取用户显示名称
  const getDisplayName = () => {
    if (!user) return '游客';
    return user.name || user.nickname || `用户${user.id}`;
  };

  // 获取用户头像
  const getAvatar = () => {
    if (!user) return null;
    return user.avatar;
  };

  // 检查是否有微信绑定
  const hasWechat = () => {
    if (!user) return false;
    return user.has_wechat === true;
  };

  // 检查邮箱是否已验证
  const isEmailVerified = () => {
    if (!user) return false;
    // 暂时返回false，因为接口不再返回此字段
    return false;
  };

  // 检查手机是否已验证
  const isPhoneVerified = () => {
    if (!user) return false;
    // 暂时返回false，因为接口不再返回此字段
    return false;
  };

  // 获取认证头
  const getAuthHeaders = () => {
    return authManager.getAuthHeaders();
  };

  // 检查token是否即将过期
  const isTokenExpiringSoon = () => {
    return authManager.isTokenExpiringSoon();
  };

  // 刷新token
  const refreshToken = async () => {
    try {
      const success = await authManager.refreshToken();
      if (success) {
        // 刷新token后，通过API获取最新的用户信息
        const updatedUser = await authManager.getUserFromAPI();
        if (updatedUser) {
          setUser(updatedUser);
          return true;
        } else {
          return false;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const value = {
    user,
    isLoading,
    isLoggedIn,
    login,
    logout,
    updateUser,
    getDisplayName,
    getAvatar,
    hasWechat,
    isEmailVerified,
    isPhoneVerified,
    getAuthHeaders,
    isTokenExpiringSoon,
    refreshToken
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
